import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Icon,
  RadioGroup,
  TextArea,
  TextInput,
  Tooltip,
} from '@makeably/creativex-design-system';
import ChannelLogo from 'components/atoms/ChannelLogo';
import styles from './CheckRow.module.css';

export const checkPropType = {
  altText: PropTypes.string,
  brands: PropTypes.arrayOf(PropTypes.string),
  challenged: PropTypes.bool,
  challengeReviewed: PropTypes.bool,
  channels: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string.isRequired,
  detailName: PropTypes.string,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  passed: PropTypes.bool.isRequired,
  placements: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf(['guideline', 'category', 'regulatory', 'hypothesis']).isRequired,
};

export const challengePropType = {
  challenged: PropTypes.bool.isRequired,
  exemptionReason: PropTypes.string.isRequired,
  isError: PropTypes.bool.isRequired,
  matchingAssetId: PropTypes.string.isRequired,
  reason: PropTypes.string.isRequired,
};

const propTypes = {
  challenge: PropTypes.shape(challengePropType).isRequired,
  challengeReasons: PropTypes.objectOf(PropTypes.string).isRequired,
  guidelineCheck: PropTypes.shape(checkPropType).isRequired,
  isAdminUser: PropTypes.bool.isRequired,
  isCheckGroup: PropTypes.bool.isRequired,
  onExemptionReasonChange: PropTypes.func.isRequired,
  onGuidelineClick: PropTypes.func.isRequired,
  onMatchingAssetIdChange: PropTypes.func.isRequired,
  onReasonChange: PropTypes.func.isRequired,
};

const label = (
  <div className={styles.label}>
    CreativeX ID
    <Tooltip ariaLabelSubject="CreativeX ID" label="Add the CreativeX ID of the identical asset here" />
  </div>
);

function getReasons(challengeReasons, isAdminUser, checkPassed) {
  const reasonOptions = [];

  if (checkPassed) {
    reasonOptions.push('shouldNotHavePassed', 'identicalCreativeFailed');
  } else {
    reasonOptions.push('shouldHavePassed', 'identicalCreativePassed');
  }

  if (isAdminUser) {
    reasonOptions.push('exemption');
  }

  return reasonOptions.map((reason) => (
    {
      label: challengeReasons[reason],
      value: reason,
    }
  ));
}

function renderDetail(title, value) {
  return (
    <div className={styles.detail}>
      <div className="t-caption-1">
        { title }
      </div>
      { value }
    </div>
  );
}

function renderChannelLogos(channels) {
  return (
    <div>
      { channels.map((channel) => (
        <ChannelLogo
          key={channel}
          channel={channel}
          size="size-24"
        />
      )) }
    </div>
  );
}

function renderDetailList(details) {
  if (!details || details.length === 0) {
    return 'N/A';
  }
  return details.map((detail) => (
    <div key={detail}>
      { detail }
    </div>
  ));
}

function CheckRow({
  challenge: {
    challenged,
    exemptionReason,
    isError,
    matchingAssetId,
    reason,
  },
  challengeReasons,
  guidelineCheck: {
    brands,
    channels,
    description,
    id,
    name,
    passed,
    placements,
    challengeReviewed,
    challenged: previouslyChallenged,
  },
  isAdminUser,
  onExemptionReasonChange,
  onGuidelineClick,
  onMatchingAssetIdChange,
  onReasonChange,
  isCheckGroup,
}) {
  const [expanded, setExpanded] = useState(false);

  const showExtra = !previouslyChallenged && challenged;
  const showDetails = expanded || showExtra || isCheckGroup;
  const isIdenticalReason = ['identicalCreativePassed', 'identicalCreativeFailed'].includes(reason);
  const isExemptionReason = reason === 'exemption';

  const classes = classNames(
    styles.clickableDiv,
    { [styles.disabled]: previouslyChallenged },
  );

  let disabledText = 'This guideline is currently under review';
  if (challengeReviewed) disabledText = 'This guideline has already been reviewed';

  const handleRowClick = () => {
    if (previouslyChallenged) return;
    setExpanded((open) => !open);
  };

  const handleCheckboxChange = (e) => {
    setExpanded(!challenged);
    onGuidelineClick(e);
  };

  const renderChallengeDetails = () => (
    <div className="u-flexColumn u-gap-24 u-marginTop-24">
      { !isCheckGroup && renderDetail('Definition', description) }
      <div className={styles.details}>
        { renderDetail('Brands', renderDetailList(brands)) }
        { renderDetail('Channels', renderChannelLogos(channels)) }
        { renderDetail('Placements', renderDetailList(placements)) }
      </div>
    </div>
  );

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleRowClick();
        }
      }}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <Checkbox
              checked={challenged}
              disabled={previouslyChallenged}
              label={isCheckGroup ? description : name}
              onChange={handleCheckboxChange}
            />
          </div>
          <div className="u-flexRow u-gap-8">
            <Icon color={passed ? 'green' : 'red'} name={passed ? 'checkCircle' : 'xCircle'} />
            <div style={{ visibility: isCheckGroup ? 'hidden' : 'visible' }}>
              <Icon name={showDetails ? 'chevronUp' : 'chevronDown'} />
            </div>
          </div>
        </div>
        <>
          { previouslyChallenged && <p className={styles.disabled}>{ disabledText }</p> }
          { showDetails && renderChallengeDetails() }
          { showExtra && (
            <>
              <div className={styles.radioGroup}>
                <RadioGroup
                  name={`challenge_${id}`}
                  options={getReasons(challengeReasons, isAdminUser, passed)}
                  value={reason}
                  onChange={onReasonChange}
                />
              </div>
              { isIdenticalReason && (
                <TextInput
                  isError={isError}
                  label={label}
                  placeholder="Empty"
                  value={matchingAssetId}
                  onChange={onMatchingAssetIdChange}
                />
              ) }
              { isExemptionReason && (
                <TextArea
                  placeholder="Please provide information here to help our team understand why this creative should be exempted."
                  size="large"
                  value={exemptionReason}
                  onChange={onExemptionReasonChange}
                />
              ) }
            </>
          ) }
        </>
      </div>
    </div>
  );
}

CheckRow.propTypes = propTypes;

export default CheckRow;
